import React, { useState } from 'react';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const PhotoUpload = ({ bookingId, category, onUploadComplete }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length === 0) return;
    
    // Limit to 5 photos
    if (uploadedPhotos.length + files.length > 5) {
      toast.error(`Maximum 5 ${category} photos allowed`);
      return;
    }

    setUploading(true);

    try {
      for (const file of files) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name} is not an image`);
          continue;
        }

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name} is too large (max 10MB)`);
          continue;
        }

        // Get Cloudinary signature
        const sigResponse = await fetch(`${API_URL}/api/photos/cloudinary/signature?folder=mobile-shine/bookings`, {
          credentials: 'include'
        });

        if (!sigResponse.ok) {
          toast.error('Failed to get upload signature');
          continue;
        }

        const sig = await sigResponse.json();

        // Upload to Cloudinary
        const formData = new FormData();
        formData.append('file', file);
        formData.append('api_key', sig.api_key);
        formData.append('timestamp', sig.timestamp);
        formData.append('signature', sig.signature);
        formData.append('folder', sig.folder);

        const uploadResponse = await fetch(
          `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
          {
            method: 'POST',
            body: formData
          }
        );

        if (!uploadResponse.ok) {
          toast.error(`Failed to upload ${file.name}`);
          continue;
        }

        const uploadData = await uploadResponse.json();

        // Save photo metadata to backend
        const saveResponse = await fetch(`${API_URL}/api/photos`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            booking_id: bookingId,
            cloudinary_public_id: uploadData.public_id,
            cloudinary_url: uploadData.secure_url,
            category: category
          })
        });

        if (saveResponse.ok) {
          const photoData = await saveResponse.json();
          setUploadedPhotos(prev => [...prev, photoData]);
          toast.success(`${file.name} uploaded successfully`);
        } else {
          toast.error(`Failed to save ${file.name} metadata`);
        }
      }

      if (onUploadComplete) {
        onUploadComplete();
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (photoId, publicId) => {
    try {
      const response = await fetch(`${API_URL}/api/photos/${photoId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setUploadedPhotos(prev => prev.filter(p => p.id !== photoId));
        toast.success('Photo deleted');
      } else {
        toast.error('Failed to delete photo');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete photo');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white capitalize">{category} Photos</h3>
        <span className="text-sm text-gray-400">{uploadedPhotos.length} / 5</span>
      </div>

      {/* Upload Button */}
      <label className={`
        flex flex-col items-center justify-center w-full h-32 
        border-2 border-dashed rounded-lg cursor-pointer
        transition-colors
        ${uploading ? 'border-gray-600 bg-gray-800/50 cursor-not-allowed' : 'border-gray-600 hover:border-red-500 bg-gray-900/50 hover:bg-gray-800/50'}
      `}>
        <div className="flex flex-col items-center justify-center pt-5 pb-6">
          {uploading ? (
            <>
              <Loader2 className="w-8 h-8 mb-2 text-gray-400 animate-spin" />
              <p className="text-sm text-gray-400">Uploading...</p>
            </>
          ) : (
            <>
              <Upload className="w-8 h-8 mb-2 text-gray-400" />
              <p className="mb-2 text-sm text-gray-400">
                <span className="font-semibold">Click to upload</span> or drag and drop
              </p>
              <p className="text-xs text-gray-500">PNG, JPG up to 10MB (max 5 photos)</p>
            </>
          )}
        </div>
        <input
          type="file"
          className="hidden"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          disabled={uploading || uploadedPhotos.length >= 5}
        />
      </label>

      {/* Uploaded Photos Grid */}
      {uploadedPhotos.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {uploadedPhotos.map((photo) => (
            <div key={photo.id} className="relative group">
              <img
                src={photo.cloudinary_url}
                alt={`${category} photo`}
                className="w-full h-32 object-cover rounded-lg border border-gray-700"
              />
              <button
                onClick={() => handleDelete(photo.id, photo.cloudinary_public_id)}
                className="absolute top-2 right-2 p-1 bg-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PhotoUpload;
